import { useCallback, useEffect, useState } from "react";

import type { HostedProviderState } from "./api";
import { recordHostedAccessResult } from "./hostedAccessStore";
import { useHostedAccountEpoch } from "./hostedProjectStore";
import {
  hostedApiErrorCode,
  hostedApiErrorMessage,
  useHostedApiClient,
} from "./useHostedApiClient";

/**
 * ChatGPT readiness as the Worker reports it.
 *
 * Codex and Claude availability comes from a linked local environment, so this
 * only covers the hosted provider. A failed read is surfaced rather than
 * silently treated as "disconnected", so the UI can say the backend is
 * unavailable instead of showing a button that cannot work.
 */
export function useHostedChatGptStatus() {
  const client = useHostedApiClient();
  const accountEpoch = useHostedAccountEpoch();
  const [state, setState] = useState<HostedProviderState | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await client.readChatGptStatus();
      setState(next);
      setErrorMessage(null);
      recordHostedAccessResult({ code: null });
    } catch (error) {
      setState(null);
      setErrorMessage(hostedApiErrorMessage(error));
      recordHostedAccessResult({
        code: hostedApiErrorCode(error),
        message: hostedApiErrorMessage(error),
      });
    }
  }, [client]);

  useEffect(() => {
    // A ChatGPT connection belongs to the signed-in account, so a switch makes
    // the current answer wrong. Clearing first means the panel never shows the
    // previous account's connected state while the new read is in flight.
    setState(null);
    setErrorMessage(null);
    void refresh();
  }, [accountEpoch, refresh]);

  return { state, errorMessage, refresh };
}
